import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown, Settings, Inbox, CreditCard, Briefcase, Landmark, HandHeart, Store } from "lucide-react";
import { NotificationBell } from "./NotificationBell";

const LOGO = "https://customer-assets.emergentagent.com/job_ai-legal-test/artifacts/trhkf0y7_justice%20ai%20logo.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const navLinks = [
    { href: "/services", label: "Legal Services" },
    { href: "/get-legal-help", label: "For Citizens" },
    { href: "/for-lawyers", label: "For Lawyers" },
    { href: "/for-ngos", label: "Justice Wallet" },
    { href: "/for-governments", label: "Institutional" },
    { href: "/how-it-works", label: "How It Works" },
    { href: "/about", label: "About" },
    { href: "/contact", label: "Contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-ink/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img
              src={LOGO}
              alt="AccessJustice.Global"
              className="h-11 w-auto transition-transform group-hover:scale-105"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-playfair text-xl font-semibold text-white tracking-tight">
                Access<span className="italic text-turquoise">Justice</span>
              </span>
              <span className="font-montserrat text-[9px] tracking-[0.32em] text-gold-200/80 uppercase mt-1">
                The OS for Justice
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.slice(0, 4).map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.href.slice(1)}`}
                className={`px-3.5 py-2 text-[12.5px] font-montserrat font-medium tracking-wider uppercase rounded-full transition-all ${
                  isActive(link.href)
                    ? "text-turquoise bg-turquoise/10 border border-turquoise/30"
                    : "text-white/70 hover:text-white hover:bg-white/5 border border-transparent"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="more-dropdown"
                  className="px-3.5 py-2 text-[12.5px] font-montserrat font-medium tracking-wider uppercase rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-all flex items-center gap-1"
                >
                  More <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-ink border-white/10 text-white">
                {navLinks.slice(4).map((link) => (
                  <DropdownMenuItem key={link.href} asChild className="focus:bg-white/5">
                    <Link
                      to={link.href}
                      data-testid={`dropdown-link-${link.href.slice(1)}`}
                      className="w-full font-montserrat text-xs uppercase tracking-wider"
                    >
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            {user && <NotificationBell />}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 hover:border-turquoise/50 hover:bg-white/5 transition-all"
                  >
                    <User className="w-4 h-4 text-turquoise" />
                    <span className="font-montserrat text-xs uppercase tracking-wider text-white max-w-[110px] truncate">
                      {user.name}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-white/60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-ink border-white/10 text-white">
                  <DropdownMenuItem asChild className="focus:bg-white/5">
                    <Link to="/dashboard" data-testid="dashboard-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {["citizen", "business", "ngo"].includes(user.role || "citizen") && (
                    <DropdownMenuItem asChild className="focus:bg-white/5">
                      <Link to="/client/hub" data-testid="client-hub-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider">
                        <Briefcase className="w-4 h-4" /> My Matters
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "lawyer" && (
                    <DropdownMenuItem asChild className="focus:bg-white/5">
                      <Link to="/lawyer/marketplace" data-testid="lawyer-marketplace-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider">
                        <Store className="w-4 h-4" /> Marketplace Desk
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild className="focus:bg-white/5">
                      <Link to="/admin/marketplace" data-testid="admin-marketplace-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider text-gold-200">
                        <Settings className="w-4 h-4" /> Marketplace Ops
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {["admin", "finance"].includes(user.role) && (
                    <DropdownMenuItem asChild className="focus:bg-white/5">
                      <Link to="/finance" data-testid="finance-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider">
                        <Landmark className="w-4 h-4" /> Finance
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "donor" && (
                    <DropdownMenuItem asChild className="focus:bg-white/5">
                      <Link to="/donor" data-testid="donor-portal-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider">
                        <HandHeart className="w-4 h-4" /> Donor Portal
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {(user.role === "citizen" || !user.role) && (
                    <DropdownMenuItem asChild className="focus:bg-white/5">
                      <Link to="/my-requests" data-testid="my-requests-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider">
                        <Inbox className="w-4 h-4" /> My Requests
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild className="focus:bg-white/5">
                      <Link to="/admin" data-testid="admin-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider text-gold-200">
                        <Settings className="w-4 h-4" /> Admin Console
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {(user.role === "lawyer" || user.role === "admin") && (
                    <>
                      <DropdownMenuItem asChild className="focus:bg-white/5">
                        <Link to="/lawyer/inbox" data-testid="lawyer-inbox-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider">
                          <Inbox className="w-4 h-4" /> Lawyer Inbox
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="focus:bg-white/5">
                        <Link to="/lawyer-subscribe" data-testid="lawyer-subscribe-link" className="flex items-center gap-2 font-montserrat text-xs uppercase tracking-wider">
                          <CreditCard className="w-4 h-4" /> Pilot Plan
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={logout}
                    data-testid="logout-btn"
                    className="focus:bg-white/5 flex items-center gap-2 text-red-400 font-montserrat text-xs uppercase tracking-wider"
                  >
                    <LogOut className="w-4 h-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" data-testid="login-btn" className="btn-aj-ghost">
                  Sign In
                </Link>
                <Link to="/ai-assistant" data-testid="get-help-btn" className="btn-aj-primary">
                  Get Legal Help
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button data-testid="mobile-menu-btn" className="p-2 rounded-md text-white hover:bg-white/5">
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] sm:w-[380px] bg-ink border-white/10 text-white">
              <div className="flex flex-col gap-2 mt-10">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-${link.href.slice(1)}`}
                    className={`px-4 py-3 text-sm font-montserrat font-medium tracking-wider uppercase rounded-lg transition-all ${
                      isActive(link.href)
                        ? "text-turquoise bg-turquoise/10 border border-turquoise/25"
                        : "text-white/75 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t border-white/10 pt-4 mt-4 flex flex-col gap-3">
                  {user ? (
                    <>
                      <Link to="/dashboard" onClick={() => setIsOpen(false)} data-testid="mobile-dashboard-btn" className="btn-aj-primary w-full">
                        <LayoutDashboard className="w-4 h-4" /> Dashboard
                      </Link>
                      <button
                        onClick={() => { logout(); setIsOpen(false); }}
                        data-testid="mobile-logout-btn"
                        className="btn-aj-secondary w-full"
                      >
                        <LogOut className="w-4 h-4" /> Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/ai-assistant" onClick={() => setIsOpen(false)} data-testid="mobile-get-help-btn" className="btn-aj-primary w-full">
                        Get Legal Help
                      </Link>
                      <Link to="/login" onClick={() => setIsOpen(false)} data-testid="mobile-login-btn" className="btn-aj-secondary w-full">
                        Sign In
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
